// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyCustomer, TyInteraction, TyLead, TyNote, TyWarrantyRecord } from "@/types";
import { dueState } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import StatusChip from "@/UI/atoms/StatusChip";
import InteractionCard from "@/UI/cards/InteractionCard";
import LeadCard from "@/UI/cards/LeadCard";
import NoteCard from "@/UI/cards/NoteCard";
import ActionsBar from "@/UI/components/ActionsBar";
import EmptyState from "@/UI/components/EmptyState";
import RecordDeals from "@/UI/components/RecordDeals";
import RecordFiles from "@/UI/components/RecordFiles";
import RecordPanel from "@/UI/components/RecordPanel";
import RecordWarranties from "@/UI/components/RecordWarranties";
import ScopeTabs from "@/UI/components/ScopeTabs";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import SelectInput from "@/UI/inputs/SelectInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import CustomerFormPopup from "@/UI/popups/CustomerFormPopup";
import HistoryPopup from "@/UI/popups/HistoryPopup";
import InteractionFormPopup from "@/UI/popups/InteractionFormPopup";
import InteractionPopup from "@/UI/popups/InteractionPopup";
import LeadFormPopup from "@/UI/popups/LeadFormPopup";
import NoteFormPopup from "@/UI/popups/NoteFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "leads" | "timeline" | "sales" | "reservations" | "warranties" | "notes" | "files";

type TyDraft = {
    stages: string[];
    sources: string[];
    closed: string;
    channels: string[];
    direction: string;
    due: string;
    subject: string;
    warrantyUnit: string;
    warrantyState: string;
};

/**
 * One person, and everything they ever asked for.
 *
 * **Each tab fetches its own list** rather than taking one bundle off the record. The enquiries and
 * the contacts both wanted filtering — by stage, by who holds it, by channel, by what is still owed
 * — and the list endpoints already know how to do all of that scoped to one customer. A bundle
 * would have meant reimplementing every one of those filters in the browser over a capped array.
 *
 * **The contact history crosses leads**, which is the whole reason an interaction carries its
 * customer as well as its lead: "when did we last speak to this man" is one query on one index
 * instead of one per deal he ever opened. Editing an entry is not offered — it belongs to the lead
 * that owns it, and a timeline assembled from several is the wrong place to change one.
 *
 * Blocking lives on the header rather than inside the edit form. It is not a detail about the
 * person, it is a decision about their enquiries, and it should cost one press and read as its own
 * act — confirmed, because the person it is done to is never told.
 */
export default function CustomerScreen() {
    const t = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { isOwner, role } = usePermission();
    const addComp = usePopup((state) => state.addComp);

    const [params, setParams] = useSearchParams();
    const [isLeaving, setIsLeaving] = useState(false);

    /**
     * The tab and the scope live in the URL and **replace** rather than push. A reload, a back
     * button or a link sent to a colleague all land where the person was, and moving between tabs
     * never stacks history somebody has to press back through to leave the record.
     */
    const tab = (params.get("tab") as TyTab) || "leads";
    const scope: "all" | "mine" = params.get("scope") === "mine" ? "mine" : "all";

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        setParams(merged, { replace: true });
    };

    const setTab = (key: TyTab) => apply({ tab: key === "leads" ? null : key });
    const setScope = (next: "all" | "mine") => apply({ scope: next === "mine" ? "mine" : null });

    // --- Filters, per tab, held here rather than in the URL: this is a record screen, and a link
    //     to it means "this person", not "this person's qualified enquiries from the catalog".
    const [isFiltering, setIsFiltering] = useState(false);
    const [stages, setStages] = useState<string[]>([]);
    const [sources, setSources] = useState<string[]>([]);
    const [closed, setClosed] = useState("0");
    const [channels, setChannels] = useState<string[]>([]);
    const [direction, setDirection] = useState("");
    const [due, setDue] = useState("");
    /** The timeline's subject: every contact, the ones before the sale, or one guarantee's thread. */
    const [subject, setSubject] = useState("");
    const [warrantyUnit, setWarrantyUnit] = useState("");
    const [warrantyState, setWarrantyState] = useState("");
    const [draft, setDraft] = useState<TyDraft>({
        stages: [],
        sources: [],
        closed: "0",
        channels: [],
        direction: "",
        due: "",
        subject: "",
        warrantyUnit: "",
        warrantyState: "",
    });

    const canManage = isOwner || role === "manager";
    const customer = useFetch<{ customer: TyCustomer }>(`/crm/customers/${id ?? "x"}`, {}, false);
    const leads = useFetch<{ leads: TyLead[] }>("/crm/leads", {}, false);
    const interactions = useFetch<{ interactions: TyInteraction[] }>("/crm/interactions", {}, false);
    /**
     * Every guarantee this person holds, read once for the two filters that need it — the warranty
     * tab's unit picker and the timeline's subject picker. It is also what decides whether the
     * timeline offers a warranty filter at all: before a deal there are none, and a filter with one
     * option is a control that cannot do anything.
     */
    const holdings = useFetch<{ warranties: TyWarrantyRecord[] }>("/sales/warranties", {}, false);
    const action = useFetch("/crm/customers", {}, false);

    const load = () => customer.invoke({ path: `/crm/customers/${id}` });

    const loadLeads = () => {
        const query: Record<string, unknown> = { customer: id, limit: constants.maxPageSize, closed };
        if (scope === "mine") query.mine = 1;
        if (stages.length) query.stage = stages.join(",");
        if (sources.length) query.source = sources.join(",");
        leads.invoke({ query });
    };

    const loadInteractions = () => {
        const query: Record<string, unknown> = { customer: id, limit: constants.maxPageSize };
        if (scope === "mine") query.mine = 1;
        if (channels.length) query.channel = channels.join(",");
        if (direction) query.direction = direction;
        if (due) query.due = due;
        // `none` is everything that was not about a guarantee — their life before the sale.
        if (subject) query.warranty = subject;
        interactions.invoke({ query });
    };

    useEffect(() => {
        if (!id) return;
        load();
        holdings.invoke({ query: { customer: id, limit: constants.maxPageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id) return;
        if (tab === "leads") loadLeads();
        if (tab === "timeline") loadInteractions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, tab, scope, stages.join(","), sources.join(","), closed, channels.join(","), direction, due, subject]);

    useEffect(() => {
        if (!action.succeededAt) return;
        if (isLeaving) {
            navigate("/crm");
            return;
        }
        load();
        if (tab === "leads") loadLeads();
        if (tab === "timeline") loadInteractions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const record = customer.data?.customer;

    if (customer.loading && !record) return <p className="customer-screen__state">{t.common.loading}</p>;
    if (customer.error) return <p className="customer-screen__state customer-screen__state--error">{customer.error}</p>;
    if (!record) return <EmptyState icon="team" title={t.crm.customerNotFound} description={t.crm.customerNotFoundHint} />;

    const rows = leads.data?.leads ?? [];
    const contacts = interactions.data?.interactions ?? [];
    /** Owed above, done below — the same list cut by whether its entries have an outcome yet. */
    const booked = contacts.filter((entry) => dueState(entry) !== "done").sort((a, b) => +new Date(a.date) - +new Date(b.date));
    const happened = contacts.filter((entry) => dueState(entry) === "done");

    /** What they hold, and the units behind it — both pickers read off the same one request. */
    const warranties = holdings.data?.warranties ?? [];
    const hasWarranties = warranties.length > 0;
    const heldUnits = [
        ...new Map(
            warranties
                .filter((entry) => typeof entry.unit === "object" && entry.unit)
                .map((entry) => {
                    const unit = entry.unit as { _id: string; number: string };
                    return [unit._id, unit] as const;
                })
        ).values(),
    ];

    /**
     * Which guarantee a contact was about, for the one extra word the card carries. The timeline
     * runs across both halves of the person's life with the company, so a row from after the sale
     * has to say so — otherwise a warranty call reads as one more conversation about an enquiry.
     */
    const subjectTag = (entry: TyInteraction): { label: string; icon: TyIconName } | undefined => {
        const held = typeof entry.warranty === "object" && entry.warranty ? entry.warranty : null;
        if (held) {
            const unit = typeof held.unit === "object" && held.unit ? held.unit.number : null;
            const label = [t.warranties.one, t.properties.warrantyTypes[held.type], unit].filter(Boolean).join(" · ");
            return { label, icon: "crown" };
        }

        // The other half: a contact that belongs to an enquiry. Everything else is with the person
        // and about nothing in particular — chasing a payment, arranging a handover — and says so
        // by carrying no subject at all.
        return entry.lead ? { label: t.crm.subjectLead, icon: "crm" } : undefined;
    };

    /** "Structure · 204" — a guarantee has no name, so it is what it covers and where. */
    const warrantyLabel = (entry: TyWarrantyRecord): string => {
        const unit = typeof entry.unit === "object" && entry.unit ? entry.unit.number : null;
        const type = t.properties.warrantyTypes[entry.type];
        return unit ? `${type} · ${unit}` : type;
    };

    // --- Actions ---------------------------------------------------------------------------------

    const confirmBlock = () =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: record.isBlocked ? t.crm.unblock : t.crm.block,
                message: (record.isBlocked ? t.crm.confirmUnblock : t.crm.confirmBlock).replace("{name}", record.name),
                confirmLabel: record.isBlocked ? t.crm.unblock : t.crm.block,
                onConfirm: () => action.invoke({ method: "PATCH", path: `/crm/customers/${record._id}`, data: { isBlocked: !record.isBlocked } }),
            },
        });

    const openLeadForm = (lead?: TyLead) =>
        addComp({
            name: "lead-form",
            Component: LeadFormPopup,
            place: "end",
            anim: "endSlide",
            details: { lead: lead ?? null, customer: lead ? null : record, onDone: loadLeads },
        });

    /**
     * The contact, read whole — **with its actions, the same ones a lead's own timeline offers.**
     * This screen used to withhold them on the argument that an entry belongs to the lead that owns
     * it, but a person's timeline is where most of them are met, and a popup that can be opened
     * from two places and only acts in one is a popup somebody learns to distrust. What may still
     * be changed is the API's business anyway: a contact freezes four fields once its date passes.
     */
    const openInteractionDetails = (interaction: TyInteraction) =>
        addComp({
            name: "interaction",
            Component: InteractionPopup,
            anim: "scale",
            details: {
                interaction,
                subject: subjectTag(interaction)?.label ?? null,
                // Still planned opens on the "what happened" side, which is what its button says.
                onEdit: () => logInteraction(interaction, dueState(interaction) === "done" ? undefined : false),
                onRemove: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.common.remove,
                            message: t.crm.confirmRemoveInteraction,
                            confirmLabel: t.common.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/crm/interactions/${interaction._id}` }),
                        },
                    }),
            },
        });

    /**
     * Writing one down. `planning: false` opens it on the happened side, which is what the card's
     * own button says; an entry already closed out is edited on whichever side it belongs to.
     */
    const logInteraction = (interaction: TyInteraction, planning?: boolean) =>
        addComp({
            name: "interaction-form",
            Component: InteractionFormPopup,
            anim: "scale",
            details: { interaction, planning, onDone: loadInteractions },
        });

    const openNote = (note?: TyNote) =>
        addComp({
            name: "note-form",
            Component: NoteFormPopup,
            anim: "scale",
            details: { basePath: `/crm/customers/${record._id}`, note: note ?? null, onDone: load },
        });

    const openLeadActions = (lead: TyLead, anchor: DOMRect) => {
        const actions: TyAction[] = [];
        if (!lead.closed) actions.push({ key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openLeadForm(lead) });

        addComp({
            name: "lead-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    const openMore = (anchor: DOMRect) => {
        const actions: TyAction[] = [
            { key: "block", label: record.isBlocked ? t.crm.unblock : t.crm.block, icon: "clear", onSelect: confirmBlock },
            {
                key: "remove",
                label: t.common.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.common.remove,
                            message: t.crm.confirmRemoveCustomer.replace("{name}", record.name),
                            confirmLabel: t.common.remove,
                            onConfirm: () => {
                                setIsLeaving(true);
                                action.invoke({ method: "DELETE", path: `/crm/customers/${record._id}` });
                            },
                        },
                    }),
            },
        ];

        addComp({
            name: "customer-more",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    // --- The bar ---------------------------------------------------------------------------------

    const hasFilters =
        tab === "leads"
            ? stages.length > 0 || sources.length > 0 || closed !== "0"
            : tab === "warranties"
              ? !!warrantyUnit || !!warrantyState
              : channels.length > 0 || !!direction || !!due || !!subject;
    const isFilterable = tab === "leads" || tab === "timeline" || tab === "warranties";
    /** A warranty has no owner, so "mine" has nothing to mean on that tab. */
    const isScoped = tab === "leads" || tab === "timeline";

    const applyDraft = () => {
        setIsFiltering(false);
        setStages(draft.stages);
        setSources(draft.sources);
        setClosed(draft.closed);
        setChannels(draft.channels);
        setDirection(draft.direction);
        setDue(draft.due);
        setSubject(draft.subject);
        setWarrantyUnit(draft.warrantyUnit);
        setWarrantyState(draft.warrantyState);
    };

    const clearDraft = () => {
        const empty: TyDraft = {
            stages: [],
            sources: [],
            closed: "0",
            channels: [],
            direction: "",
            due: "",
            subject: "",
            warrantyUnit: "",
            warrantyState: "",
        };
        setDraft(empty);
        setIsFiltering(false);
        setStages([]);
        setSources([]);
        setClosed("0");
        setChannels([]);
        setDirection("");
        setDue("");
        setSubject("");
        setWarrantyUnit("");
        setWarrantyState("");
    };

    /** One add button, doing whatever the tab is for. A new enquiry already knows whose it is. */
    const onAdd =
        tab === "notes"
            ? () => openNote()
            : tab === "leads"
              ? () => openLeadForm()
              : tab === "timeline"
                ? () => {
                      // Against their open enquiry when there is one, against the person when there
                      // is not — chasing a payment is a real contact with no deal behind it.
                      const open = rows.find((lead) => !lead.closed);
                      addComp({
                          name: "interaction-form",
                          Component: InteractionFormPopup,
                          anim: "scale",
                          details: { leadId: open?._id ?? null, customerId: record._id, onDone: loadInteractions },
                      });
                  }
                : undefined;

    return (
        <section className="customer-screen">
            <RecordPanel
                avatar={<Avatar name={record.name} picture={record.picture} size={46} rounded bordered />}
                title={record.name}
                // A number reads left to right in every language, so it carries its own direction
                // rather than inheriting the page's and swapping its own ends.
                subtitle={<bdi dir="ltr">{record.phone}</bdi>}
                chips={
                    <>
                        <StatusChip tone={record.status === "client" ? "available" : "accent"} label={t.crm.statuses[record.status]} />
                        {record.isBlocked && <StatusChip tone="danger" label={t.crm.blocked} />}
                    </>
                }
                onHistory={
                    canManage
                        ? () =>
                              // A side panel, like a project's and a unit's. A history is a long
                              // list you read down and compare against the record still on screen,
                              // which a centred dialog covers.
                              addComp({
                                  name: "history",
                                  Component: HistoryPopup,
                                  place: "end",
                                  anim: "endSlide",
                                  details: { title: record.name, path: `/crm/customers/${record._id}/updates` },
                              })
                        : undefined
                }
                onEdit={() =>
                    addComp({ name: "customer-form", Component: CustomerFormPopup, anim: "scale", details: { customer: record, onDone: load } })
                }
                onMore={openMore}
            >
                <ActionsBar
                    tabs={[
                        { key: "leads", label: t.crm.interests },
                        { key: "timeline", label: t.crm.tabs.timeline },
                        { key: "sales", label: t.sales.contracts },
                        { key: "reservations", label: t.sales.reservations },
                        { key: "warranties", label: t.warranties.tab },
                        { key: "notes", label: t.crm.tabs.notes },
                        { key: "files", label: t.crm.tabs.files },
                    ]}
                    activeTab={tab}
                    onTab={(key) => setTab(key as TyTab)}
                    addLabel={t.common.add}
                    onAdd={onAdd}
                    isFilterOpen={isFiltering}
                    onFilterOpen={
                        isFilterable
                            ? () => {
                                  setDraft({ stages, sources, closed, channels, direction, due, subject, warrantyUnit, warrantyState });
                                  setIsFiltering(true);
                              }
                            : undefined
                    }
                    hasFilters={hasFilters}
                    onFilterApply={applyDraft}
                    onFilterClear={clearDraft}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        isFilterable ? (
                            tab === "warranties" ? (
                                <>
                                    {/* Which flat. A person with three of them has three sets of
                                        guarantees, and that is the first question asked of any one. */}
                                    <SelectInput
                                        compact
                                        label={t.properties.units}
                                        value={draft.warrantyUnit}
                                        onChange={(next) => setDraft({ ...draft, warrantyUnit: next })}
                                        options={[
                                            { value: "", label: t.warranties.allUnits },
                                            ...heldUnits.map((unit) => ({ value: unit._id, label: unit.number })),
                                        ]}
                                    />
                                    <SelectInput
                                        compact
                                        label={t.crm.fields.status}
                                        value={draft.warrantyState}
                                        onChange={(next) => setDraft({ ...draft, warrantyState: next })}
                                        options={[
                                            { value: "", label: t.warranties.allStates },
                                            ...constants.warrantyStates
                                                .filter((item) => item !== "notStarted")
                                                .map((item) => ({ value: item, label: t.warranties.state[item] })),
                                        ]}
                                    />
                                </>
                            ) : tab === "leads" ? (
                                <>
                                    <MultiSelectInput
                                        compact
                                        value={draft.stages}
                                        onChange={(next) => setDraft({ ...draft, stages: next })}
                                        options={constants.leadStages.map((item) => ({ value: item, label: t.crm.stages[item] }))}
                                        label={t.crm.fields.stage}
                                    />
                                    <MultiSelectInput
                                        compact
                                        value={draft.sources}
                                        onChange={(next) => setDraft({ ...draft, sources: next })}
                                        options={constants.leadSources.map((item) => ({ value: item, label: t.crm.sources[item] }))}
                                        label={t.crm.fields.source}
                                    />
                                    <SelectInput
                                        compact
                                        label={t.crm.fields.status}
                                        value={draft.closed}
                                        onChange={(next) => setDraft({ ...draft, closed: next })}
                                        options={[
                                            { value: "0", label: t.crm.interests },
                                            { value: "1", label: t.crm.closedBadge },
                                            { value: "all", label: t.crm.all },
                                        ]}
                                    />
                                </>
                            ) : (
                                <>
                                    <MultiSelectInput
                                        compact
                                        value={draft.channels}
                                        onChange={(next) => setDraft({ ...draft, channels: next })}
                                        options={constants.interactionChannels.map((item) => ({ value: item, label: t.crm.channels[item] }))}
                                        label={t.crm.fields.channel}
                                    />
                                    <SelectInput
                                        compact
                                        label={t.crm.fields.direction}
                                        value={draft.direction}
                                        onChange={(next) => setDraft({ ...draft, direction: next })}
                                        options={[
                                            { value: "", label: t.crm.all },
                                            ...constants.interactionDirections.map((item) => ({ value: item, label: t.crm.directions[item] })),
                                        ]}
                                    />
                                    <SelectInput
                                        compact
                                        label={t.crm.due.title}
                                        value={draft.due}
                                        onChange={(next) => setDraft({ ...draft, due: next })}
                                        options={[
                                            { value: "", label: t.crm.all },
                                            { value: "open", label: t.crm.due.open },
                                            { value: "overdue", label: t.crm.due.overdue },
                                            { value: "upcoming", label: t.crm.due.upcoming },
                                        ]}
                                    />

                                    {/* What the contact was about. Absent until the person holds a
                                        guarantee: before the sale every contact is an enquiry, and
                                        a picker whose only option is "everything" is a dead control. */}
                                    {hasWarranties && (
                                        <SelectInput
                                            compact
                                            label={t.warranties.subject}
                                            value={draft.subject}
                                            onChange={(next) => setDraft({ ...draft, subject: next })}
                                            options={[
                                                { value: "", label: t.warranties.allSubjects },
                                                { value: "none", label: t.warranties.beforeSale },
                                                ...warranties.map((entry) => ({ value: entry._id, label: warrantyLabel(entry) })),
                                            ]}
                                        />
                                    )}
                                </>
                            )
                        ) : undefined
                    }
                />
            </RecordPanel>

            {action.error && <p className="customer-screen__state customer-screen__state--error">{action.error}</p>}
            {record.isBlocked && <p className="customer-screen__blocked">{t.crm.blockedHint}</p>}

            {/*
             * Mine or everybody's, outside the panel: the one filter set on arrival every day.
             *
             * **It names what it is narrowing.** The same two buttons sit over enquiries here and
             * over contacts one tab along, and "Mine" over a list of calls was the control saying
             * nothing about which list it was cutting. Warranties have no owner at all, so they get
             * no scope — hidden rather than shown doing nothing.
             */}
            {isScoped && (
                <ScopeTabs
                    value={scope}
                    onChange={setScope}
                    allLabel={tab === "timeline" ? t.crm.allContacts : t.crm.allInterests}
                    mineLabel={tab === "timeline" ? t.crm.mineContacts : t.crm.mineInterests}
                />
            )}

            {tab === "leads" && (
                /* The same card the pipeline uses, minus the name — the person is the headline
                   here. A one-line row could not say which stage, how long it had been quiet, or
                   who holds it, which is what somebody opening this tab came to find out. */
                <div className="customer-screen__grid">
                    {!rows.length && !leads.loading && (
                        <EmptyState
                            span
                            icon="crm"
                            title={t.crm.emptyPersonLeads}
                            description={t.crm.emptyPersonLeadsHint}
                            actionLabel={t.crm.addLead}
                            onAction={() => openLeadForm()}
                        />
                    )}
                    {rows.map((lead) => (
                        <LeadCard
                            key={lead._id}
                            lead={lead}
                            hidePerson
                            onOpen={() => navigate(`/crm/leads/${lead._id}`)}
                            onContact={
                                lead.closed
                                    ? undefined
                                    : () =>
                                          addComp({
                                              name: "interaction-form",
                                              Component: InteractionFormPopup,
                                              anim: "scale",
                                              details: { leadId: lead._id, onDone: loadLeads },
                                          })
                            }
                            onActions={(anchor) => openLeadActions(lead, anchor)}
                        />
                    ))}
                </div>
            )}

            {tab === "timeline" && (
                <div className="customer-screen__grid">
                    {/* What is still owed comes first, here as well as on a lead — a person's own
                        screen is where somebody checks they owe nothing. */}
                    {booked.map((entry) => (
                        <InteractionCard
                            key={entry._id}
                            interaction={entry}
                            tag={subjectTag(entry)}
                            onOpen={() => openInteractionDetails(entry)}
                            onLog={() => logInteraction(entry, false)}
                        />
                    ))}

                    {!contacts.length && !interactions.loading && (
                        <EmptyState span icon="phone" title={t.crm.emptyInteractions} description={t.crm.emptyInteractionsHint} />
                    )}

                    {happened.map((entry) => (
                        <InteractionCard key={entry._id} interaction={entry} tag={subjectTag(entry)} onOpen={() => openInteractionDetails(entry)} />
                    ))}
                </div>
            )}

            {tab === "notes" && (
                <div className="customer-screen__grid">
                    {!(record.notes ?? []).length && (
                        <EmptyState span icon="edit" title={t.crm.emptyNotes} actionLabel={t.crm.addNote} onAction={() => openNote()} />
                    )}
                    {(record.notes ?? []).map((entry) => (
                        <NoteCard
                            key={entry._id}
                            note={entry}
                            onOpen={() => openNote(entry)}
                            onRemove={() =>
                                addComp({
                                    name: "confirm",
                                    Component: ConfirmPopup,
                                    anim: "scale",
                                    details: {
                                        title: t.common.remove,
                                        message: t.crm.confirmRemoveNote,
                                        confirmLabel: t.common.remove,
                                        onConfirm: () => action.invoke({ method: "DELETE", path: `/crm/customers/${record._id}/notes/${entry._id}` }),
                                    },
                                })
                            }
                        />
                    ))}
                </div>
            )}

            {/* Everything they have signed, with the payment plans that came out of it. */}
            {tab === "sales" && <RecordDeals customerId={record._id} show="contracts" reloadAt={action.succeededAt} />}

            {/* Holds, on their own. **A reservation is not a contract** — it means the flat is being
                kept for them while they decide, not that it is theirs — and running the two together
                answered "what do they own" with a maybe. Cancelled and lapsed holds live here too:
                a person who has let three go is exactly the person to know about before a fourth. */}
            {tab === "reservations" && <RecordDeals customerId={record._id} show="reservations" reloadAt={action.succeededAt} />}

            {/* Every guarantee they hold, across every unit they bought. The same component the
                unit's page mounts, called through the CRM's own section — each card names its unit,
                because a person with three flats has three sets of these. */}
            {tab === "warranties" && (
                <RecordWarranties
                    basePath="/sales"
                    customerId={record._id}
                    unitFilter={warrantyUnit}
                    stateFilter={warrantyState}
                    reloadAt={holdings.succeededAt}
                />
            )}

            {/* The same drive rows a project or a unit shows, filtered to this person — ID copies,
                a signed offer, whatever the desk scanned. */}
            {tab === "files" && <RecordFiles ownerModel="Customer" ownerId={record._id} />}
        </section>
    );
}
